import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Loader2, CheckCircle, AlertCircle, Filter } from 'lucide-react';

interface DashboardFilters {
  department_filter?: string;
  priority_filter?: string[];
  agency_filter?: string[];
}

export function DashboardPreferences() {
  const { user, organization } = useAuth();
  const [preferences, setPreferences] = useState<DashboardFilters>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get available options from organization
  const departments = organization?.settings?.departments_enabled || [];
  const agencies = organization?.settings?.default_agencies || [];
  const priorities = ['Critical', 'High', 'Medium', 'Low'];

  // Load current preferences
  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const prefs = await apiClient.getUserPreferences();
      const dashboardFilters = prefs.dashboard_filters || {};

      // Ensure arrays are actually arrays
      setPreferences({
        department_filter: dashboardFilters.department_filter || '',
        priority_filter: Array.isArray(dashboardFilters.priority_filter)
          ? dashboardFilters.priority_filter
          : [],
        agency_filter: Array.isArray(dashboardFilters.agency_filter)
          ? dashboardFilters.agency_filter
          : [],
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load preferences');
      console.error('Error loading preferences:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await apiClient.updateUserPreferences({
        dashboard_filters: preferences,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePriorityToggle = (priority: string) => {
    const current = preferences.priority_filter || [];
    const updated = current.includes(priority)
      ? current.filter(p => p !== priority)
      : [...current, priority];
    setPreferences({ ...preferences, priority_filter: updated });
  };

  const handleAgencyToggle = (agency: string) => {
    const current = preferences.agency_filter || [];
    const updated = current.includes(agency)
      ? current.filter(a => a !== agency)
      : [...current, agency];
    setPreferences({ ...preferences, agency_filter: updated });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Filter className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">Dashboard Filter Preferences</h2>
        </div>

        <div className="space-y-6">
          {/* Department Filter */}
          {departments.length > 0 && (
            <div>
              <Label htmlFor="department">Department Filter</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Filter dashboard to show only items for a specific department
              </p>
              <select
                id="department"
                value={preferences.department_filter || ''}
                onChange={(e) => setPreferences({ ...preferences, department_filter: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Priority Levels */}
          <div>
            <Label>Priority Levels</Label>
            <p className="text-sm text-muted-foreground mb-3">
              Select which priority levels to show on your dashboard
            </p>
            <div className="space-y-2">
              {priorities.map((priority) => (
                <div key={priority} className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id={`priority-${priority}`}
                    checked={(preferences.priority_filter || []).includes(priority)}
                    onChange={() => handlePriorityToggle(priority)}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <Label htmlFor={`priority-${priority}`} className="cursor-pointer font-normal">
                    {priority}
                  </Label>
                </div>
              ))}
              {(preferences.priority_filter || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  No filters selected - all priorities will be shown
                </p>
              )}
            </div>
          </div>

          {/* Agency Filter */}
          {agencies.length > 0 && (
            <div>
              <Label>Agency Filter</Label>
              <p className="text-sm text-muted-foreground mb-3">
                Select which regulatory agencies to track
              </p>
              <div className="space-y-2">
                {agencies.map((agency) => (
                  <div key={agency} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id={`agency-${agency}`}
                      checked={(preferences.agency_filter || []).includes(agency)}
                      onChange={() => handleAgencyToggle(agency)}
                      className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <Label htmlFor={`agency-${agency}`} className="cursor-pointer font-normal">
                      {agency}
                    </Label>
                  </div>
                ))}
                {(preferences.agency_filter || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">
                    No filters selected - all agencies will be shown
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Preview */}
          <div className="bg-muted/30 p-4 rounded-lg">
            <h3 className="text-sm font-semibold mb-2">Preview</h3>
            <p className="text-sm text-muted-foreground">
              Your dashboard will show:{' '}
              <span className="font-medium text-foreground">
                {preferences.department_filter || 'All Departments'}
              </span>
              {Array.isArray(preferences.priority_filter) && preferences.priority_filter.length > 0 && (
                <>
                  {' | '}
                  <span className="font-medium text-foreground">
                    {preferences.priority_filter.join(', ')} Priority
                  </span>
                </>
              )}
              {Array.isArray(preferences.agency_filter) && preferences.agency_filter.length > 0 && (
                <>
                  {' | '}
                  <span className="font-medium text-foreground">
                    {preferences.agency_filter.join(', ')}
                  </span>
                </>
              )}
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Preferences saved successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end pt-4">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Preferences'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

